/* eslint-disable no-lonely-if */
/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Chip } from '@mui/material';
import pluralize from 'pluralize';
import IntervalTimer from 'react-interval-timer';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import moment from 'utils/moment';
import numeral from 'utils/numeral';
import {
  ACTIVATION,
  CONNEXION,
  CUSTOMERS,
  IMPORTMODEL,
  INSIGHT,
  INSIGHTUSERTARGET
} from 'utils/rest';

import { Divider, Text, Tooltip } from 'components/DataDisplay';
import { Container, Item, Row } from 'components/Layout';

import {
  enrichConversionLocations,
  getGeoTargetingLabel,
  getGeoTargetingSignature,
  mockedObjectives,
  PENDING_UPSERT_STATUSES
} from '../../utils';

import buildBudgetVersions from './components/buildBudgetVersions';
import { InvoiceItem } from './components';
import ModalRecap from './ModalRecap';
import ModalSetup from './ModalSetup';

const findPendingUpsertTask = upsertTasks =>
  upsertTasks.reduce((latestTask, task) => {
    if (!PENDING_UPSERT_STATUSES.includes(task.status)) {
      return latestTask;
    }
    if (
      !latestTask ||
      new Date(task.createdAt) > new Date(latestTask.createdAt)
    ) {
      return task;
    }
    return latestTask;
  }, null);

const getCategoryKeysSignature = (categoryKeys = []) =>
  [...categoryKeys].sort().join('|');

const getChangedCategoryMedias = (task, campaign) => {
  const requestedCategoryKeys = task.activationCategoryKeys || {};
  const currentCategoryKeys = campaign.activationCategoryKeys || {};
  return Object.keys(requestedCategoryKeys).reduce((medias, mediaKey) => {
    if (
      getCategoryKeysSignature(requestedCategoryKeys[mediaKey]) !==
      getCategoryKeysSignature(currentCategoryKeys[mediaKey])
    ) {
      medias.push(mediaKey);
    }
    return medias;
  }, []);
};

const CREATIVE_TEXT_FIELD_LABELS = {
  title: 'Titre',
  mainText: 'Texte principal',
  description: 'Description',
  buttonLink: 'URL'
};

const buildFieldDiff = (oldValue, newValue) => {
  const previousValue = oldValue || null;
  const nextValue = newValue || null;
  if (!nextValue || nextValue === previousValue) {
    return null;
  }
  return { old: previousValue, new: nextValue };
};

// Le texte en place est celui de la créa de la campagne, qui surcharge la bibliothèque partagée
const getCurrentCreativeTextValue = (currentAd, mediaKey, textFieldKey) => {
  const applied = (currentAd?.metaData || []).find(
    entry => entry.key === textFieldKey
  );
  if (applied) {
    return applied.value;
  }
  const entries = currentAd?.availableCreative?.mediaDatas?.[0]?.[mediaKey];
  if (!Array.isArray(entries)) {
    return null;
  }
  const found = entries.find(entry => entry.key === textFieldKey);
  return found ? found.value : null;
};

const getCreativeTextFieldDiffs = (creative, currentAd, mediaKey) => {
  const updatedTextFields = creative.wording || {};
  return Object.keys(updatedTextFields).reduce((fields, textFieldKey) => {
    const diff = buildFieldDiff(
      getCurrentCreativeTextValue(currentAd, mediaKey, textFieldKey),
      updatedTextFields[textFieldKey]
    );
    if (diff) {
      fields.push({
        key: textFieldKey,
        label: CREATIVE_TEXT_FIELD_LABELS[textFieldKey] || textFieldKey,
        diff
      });
    }
    return fields;
  }, []);
};

const getPlanMediaBudget = (withDataPlan, mediaKey) => {
  const channels = withDataPlan?.channels || [];
  const traditionalChannel = channels.find(channel => channel.key === mediaKey);
  if (traditionalChannel) {
    return traditionalChannel.budget?.media || 0;
  }
  return channels.reduce(
    (planTotal, channel) =>
      planTotal +
      (channel.mediaData || []).reduce((channelTotal, entry) => {
        if (entry.media !== mediaKey || entry.enabled === false) {
          return channelTotal;
        }
        return channelTotal + (entry.budget?.media || 0);
      }, 0),
    0
  );
};

const isSameDay = (before, after) => {
  if (!before && !after) {
    return true;
  }
  if (!before || !after) {
    return false;
  }
  return moment(before).isSame(moment(after), 'day');
};

const getCurrentBroadcastDates = ({ campaign, mediaOrder, mediaKey }) => {
  const broadcast = (campaign.mediaBroadcastDates || []).find(
    entry => entry.media === mediaKey
  );
  return {
    start: broadcast?.startDate || mediaOrder.startDate || campaign.start,
    end: broadcast?.endDate || mediaOrder.endDate || campaign.end
  };
};

const getMediaPendingChange = ({
  mediaKey,
  mediaOrder,
  mediaAds,
  task,
  campaign,
  geoTargeting
}) => {
  const broadcast = (task.mediaBroadcastDates || []).find(
    entry => entry.media === mediaKey
  );
  const taskCreatives = (task.creatives || []).filter(
    creative => creative.media === mediaKey
  );

  const { modifiedAds, newCreatives, toggledAds } = taskCreatives.reduce(
    (acc, creative) => {
      if (creative.isNew) {
        acc.newCreatives.push({
          name: creative.name,
          format: creative.isCarousel ? 'Carrousel' : 'Image'
        });
        return acc;
      }
      const currentAd = mediaAds.find(
        ad =>
          ad.availableCreativeId === creative.id ||
          ad.availableCreative?.name === creative.name
      );

      const name = creative.name || currentAd?.availableCreative?.name || '';
      const requestedActive = creative.active !== false;
      if (currentAd && requestedActive !== (currentAd.active !== false)) {
        acc.toggledAds.push({ name, active: requestedActive });
      }
      const fields = getCreativeTextFieldDiffs(creative, currentAd, mediaKey);
      if (fields.length > 0) {
        acc.modifiedAds.push({ name, fields });
      }
      return acc;
    },
    { modifiedAds: [], newCreatives: [], toggledAds: [] }
  );

  const addedMediaBudget = getPlanMediaBudget(task.withDataPlan, mediaKey);
  const currentMediaBudget = getPlanMediaBudget(
    campaign.withDataPlan,
    mediaKey
  );
  const budget = addedMediaBudget
    ? {
        old: currentMediaBudget,
        new: currentMediaBudget + addedMediaBudget
      }
    : null;

  const currentDates = getCurrentBroadcastDates({
    campaign,
    mediaOrder,
    mediaKey
  });
  const isDatesChanged =
    !!broadcast &&
    !(
      isSameDay(currentDates.start, broadcast.startDate) &&
      isSameDay(currentDates.end, broadcast.endDate)
    );
  const dates = isDatesChanged
    ? {
        old: currentDates,
        new: { start: broadcast.startDate, end: broadcast.endDate }
      }
    : null;

  if (
    !budget &&
    !dates &&
    !geoTargeting &&
    !modifiedAds.length &&
    !newCreatives.length &&
    !toggledAds.length
  ) {
    return null;
  }
  return { budget, dates, geoTargeting, modifiedAds, newCreatives, toggledAds };
};

const ModalParams = props => {
  const { themeColors } = useTheme();
  const { domainConfig } = useApp();

  const {
    config,
    campaign,
    setupPendingTooltip,
    selectedSection,
    onUpsertBadgeChange
  } = props;
  const [isExpandedSetupCampaign, setIsExpandedSetupCampaign] = useState(true);
  const [isExpandedSetupAccount, setIsExpandedSetupAccount] = useState(true);
  const [mediaOrders, setMediaOrders] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [ads, setAds] = useState([]);
  const [availableCreatives, setAvailableCreatives] = useState([]);
  const [mediaLogosToDisplay, setMediaLogosToDisplay] = useState([]);
  const [activableMedias, setActivableMedias] = useState([]);
  const [importModels, setImportModels] = useState([]);
  const [campaignSetupTasks, setCampaignSetupTasks] = useState([]);
  const [updatePlatforms, setUpdatePlatforms] = useState(false);
  const [platformsToSave, setPlatformsToSave] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [connexions, setConnexions] = useState([]);
  const [activationShopGroups, setActivationShopGroups] = useState([]);
  const [insightUserTarget, setInsightUserTarget] = useState([]);
  const [mandatsCampaign, setMandatsCampaign] = useState([]);
  const [activationCategoryKeys, setActivationCategoryKeys] = useState({});
  const [upsertTasks, setUpsertTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [insightShops, setInsightShops] = useState([]);
  const [requestedInsightShops, setRequestedInsightShops] = useState([]);
  const previousPendingUpsertTaskIdRef = useRef(null);
  const upsertSelectionKeyRef = useRef(null);
  const [canSetupGlobally, setCanSetupGlobally] = useState(true);
  const [isExpandedDefaultGeoZone, setIsExpandedDefaultGeoZone] = useState(
    true
  );
  const [overrideDisabledGeoZone, setOverrideDisabledGeoZone] = useState(true);
  const [formattedPlatforms, setFormattedPlatforms] = useState([]);
  const [mediaOrderObjectives, setMediaOrderObjectives] = useState([]);
  const [matchedDepartments, setMatchedDepartments] = useState([]);
  const [selectedMediaOrderIds, setSelectedMediaOrderIds] = useState([]);
  const [canLinkMeta, setCanLinkMeta] = useState(false);
  const [isMetaLinked, setIsMetaLinked] = useState(false);
  const [blockedPagesMedias, setBlockedPagesMedias] = useState([]);
  const [defaultGeoZoneType, setDefaultGeoZoneType] = useState(null);
  const [defaultGeoZoneValues, setDefaultGeoZoneValues] = useState(null);
  const [disabledGeoZone, setDisabledGeoZone] = useState(true);

  const allPlatformAccounts = config.groups.flatMap(group =>
    (group.platformAccounts || []).map(account => ({
      ...account,
      groupName: group.name
    }))
  );

  const allPlatformPages = config.groups.flatMap(group =>
    (group.platformPages || []).map(page => ({
      ...page,
      groupName: group.name
    }))
  );

  const hasMetaPage = allPlatformPages.reduce(
    (acc, curr) => {
      if (curr.media === 'facebook') {
        acc.facebook = true;
      }
      if (curr.media === 'instagram') {
        acc.instagram = true;
      }
      return acc;
    },
    { facebook: false, instagram: false }
  );

  const geoZones = [
    { value: 'postalCode', label: 'Code postal' },
    { value: 'radius', label: 'Rayon' }
  ];

  const { mediaLogos = [] } = config;
  const campaignId = campaign.id || campaign._id;
  const campaignObjective = campaign.objective || '';

  const isMeta = media => ['instagram', 'facebook'].includes(media);

  const getDepartments = async (countryCode = 'FR') => {
    const country = campaign?.country || countryCode;

    if (campaign?.filters?.length > 0) {
      const matchedData = await INSIGHT.getMatchedDepartments({
        filter: campaign.filters[0],
        countryCode: country
      });
      setMatchedDepartments(matchedData.matchedDepartments || []);
    }
  };

  const findInsightShop = filter => {
    if (!filter) {
      return [];
    }

    if (Array.isArray(filter)) {
      const found = filter.flatMap(item => findInsightShop(item) ?? []);
      return found.length ? found : [];
    }

    if (filter.value) {
      return findInsightShop(filter.value);
    }

    if (filter.insightShop) {
      return filter.insightShop;
    }

    if (filter.filters) {
      return findInsightShop(filter.filters);
    }

    return [];
  };

  const getInsightShops = async items => {
    if (items.length) {
      const insightShopIds = items.flatMap(li => findInsightShop(li?.filter));
      if (insightShopIds.length > 0) {
        try {
          const data = await INSIGHT.getInsightShopByIds({
            ids: insightShopIds,
            light: true
          });
          return data;
        } catch (error) {
          console.error('Error fetching insight shop:', error);
          return [];
        }
      }
    }
    return [];
  };

  const validateDefaultGeoZone = async () => {
    const payload = {
      defaultGeoZone: {
        type: defaultGeoZoneType,
        values: defaultGeoZoneValues
      }
    };

    const geoTargetMediaOrders = mediaOrders.filter(
      mediaOrder => mediaOrder.geoTarget || isMeta(mediaOrder.activableMedia)
    );

    const geoTargetMediaOrderIds = geoTargetMediaOrders.map(mo => mo.id);

    const geoTargetLineItems = lineItems.filter(li =>
      geoTargetMediaOrderIds.includes(li.mediaOrderId)
    );

    for await (const geoTargetLineItem of geoTargetLineItems) {
      await ACTIVATION.updateLineItemById(geoTargetLineItem._id, payload);
    }
  };

  const getBudgetMediaForMediaOrder = ({ media, withDataPlan = null }) => {
    if (!withDataPlan) {
      return media.budget;
    }
    const isTraditionalMedia = withDataPlan.channels.find(
      el => el.key === media
    );
    if (isTraditionalMedia) {
      return isTraditionalMedia.budget.media;
    }
    const budgetMedia = withDataPlan.channels.reduce((acc, channel) => {
      const mediaDataFinded =
        channel?.mediaData?.find(md => md.media === media && md.enabled) ||
        null;
      if (mediaDataFinded) {
        acc += mediaDataFinded.budget.media;
      }
      return acc;
    }, 0);
    return budgetMedia;
  };

  const getBudgetMediaForLineItem = ({
    foundMediaOrders,
    foundLineItems,
    lineItem
  }) => {
    const foundLineItemsFiltered = foundLineItems.filter(
      el => el.mediaOrderId === lineItem.mediaOrderId
    );
    const mediaOrderSelected = foundMediaOrders.find(
      el => el.id === lineItem.mediaOrderId
    );
    return mediaOrderSelected?.budgetMedia || 0 / foundLineItemsFiltered.length;
  };
  const checkCampaignSetupTask = ({ masterCampaignSetupTask = [] }) => {
    if (masterCampaignSetupTask.length > 0) {
      return masterCampaignSetupTask[0];
    }
    return null;
  };

  const isSetupEnded = ({ masterCampaignSetupTask = [] }) => {
    if (masterCampaignSetupTask.length > 0) {
      if (
        ['done', 'error', 'aborted'].includes(masterCampaignSetupTask[0].status)
      ) {
        return true;
      }
      return false;
    }
    return true;
  };

  const getSetupTask = async ({
    foundCampaignSetupTasks,
    mediaOrderId,
    masterCampaignSetupTask = []
  }) => {
    const masterCampaignSetup = checkCampaignSetupTask({
      masterCampaignSetupTask
    });

    if (
      masterCampaignSetup &&
      masterCampaignSetup.mediaOrderIds.includes(mediaOrderId)
    ) {
      return masterCampaignSetup;
    }

    return (
      foundCampaignSetupTasks
        .filter(foundCampaignSetupTask =>
          foundCampaignSetupTask.mediaOrderIds.includes(mediaOrderId)
        )
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0] ||
      null
    );
  };

  const getDisplaySetup = ({
    mediaOrder,
    allActivableMedias,
    allImportModels
  }) => {
    const model = allActivableMedias.find(
      el => el.key === mediaOrder.activableMedia
    )?.model;
    const importModel = allImportModels.find(el => el.connectorName === model);
    return importModel?.canSetUp;
  };

  const getSetup = ({
    mediaOrder,
    allActivableMedias,
    allImportModels,
    allCampaignSetupTasks,
    masterCampaignSetupTask = []
  }) => {
    const canSetup = getDisplaySetup({
      mediaOrder,
      allActivableMedias,
      allImportModels
    });
    const selectedSetupTask =
      allCampaignSetupTasks
        .filter(el => el.mediaOrderId === mediaOrder.id)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0] ||
      null;
    if (canSetup) {
      const reponse = checkCampaignSetupTask({
        masterCampaignSetupTask
      });
      if (reponse) {
        if (!reponse.mediaOrderId) {
          return isSetupEnded({
            masterCampaignSetupTask
          });
        }
      }
      if (selectedSetupTask) {
        return ['done', 'error', 'aborted'].includes(selectedSetupTask.status);
      }

      return true;
    }
    return false;
  };

  const getInheritedSources = shopGroups => {
    const sources = [];
    const campaignGroup = config.groups.find(el => el._id === campaign.groupId);
    if (campaignGroup) {
      sources.push(campaignGroup);
    }

    const visitedKeys = [];
    let shopGroup = (shopGroups || []).find(el =>
      (el.shops || []).includes(campaign.activationShopId)
    );

    while (shopGroup && !visitedKeys.includes(shopGroup.key)) {
      const currentShopGroup = shopGroup;
      visitedKeys.push(currentShopGroup.key);
      const shopGroupOrganisation = config.groups.find(
        el => String(el.shopGroupId) === String(currentShopGroup._id)
      );
      if (shopGroupOrganisation) {
        sources.push(shopGroupOrganisation);
      }
      shopGroup = currentShopGroup.parent
        ? (shopGroups || []).find(el => el.key === currentShopGroup.parent)
        : null;
    }

    if (domainConfig) {
      sources.push(domainConfig);
    }
    return sources;
  };

  const findInSources = (sources, pick) =>
    sources.reduce((found, source) => found || pick(source) || null, null);

  const applyInheritedDefault = (row, sources) => {
    const isResolved = platformConfig =>
      platformConfig !== null && typeof platformConfig === 'object';

    const account =
      row.account && !isResolved(row.account.config)
        ? {
            ...row.account,
            config:
              findInSources(sources, source =>
                (source?.platformAccounts || []).find(
                  el => el.model === row.account.model
                )
              ) || row.account.config
          }
        : row.account;

    const page =
      row.page && !isResolved(row.page.config)
        ? {
            ...row.page,
            config:
              findInSources(sources, source =>
                (source?.platformPages || []).find(el => el.media === row.media)
              ) || row.page.config
          }
        : row.page;

    return { ...row, account, page };
  };

  const formatPlatform = (
    allActivableMedias,
    foundMediaOrders,
    foundImportModels,
    shopGroups
  ) => {
    const result = foundMediaOrders.reduce((acc, mediaOrder) => {
      const media = mediaOrder.activableMedia;
      const activableMedia = allActivableMedias.find(
        e => e.key === media && e.canSetUp
      );
      if (!activableMedia) {
        return acc;
      }

      const importModel = foundImportModels.find(
        e => e.connectorName === activableMedia.model
      );

      const mediaPlatforms = campaign?.platforms.filter(
        platform => platform.media === media
      );

      mediaPlatforms.forEach(platform => {
        const exist = acc.find(el => el.media === platform.media);
        if (exist) {
          exist.account =
            platform.type === 'account'
              ? { ...platform, model: importModel?.model }
              : exist.account;
          exist.page = platform.type === 'page' ? platform : exist.page;
          return;
        }
        acc.push({
          _id: platform.media,
          media: platform.media,
          account:
            platform.type === 'page'
              ? { media, type: 'account', model: importModel?.model }
              : { ...platform, model: importModel?.model },
          page: platform.type === 'account' ? null : platform,
          drawer: false
        });
      });

      if (mediaPlatforms.length === 0) {
        // on créé la platform si pas dans la campaign mais bien dans le mediaorder
        acc.push({
          _id: media,
          media,
          account: { media, type: 'account', model: importModel?.model },
          page: activableMedia?.page ? { media, type: 'page' } : null,
          drawer: false,
          campaignId: mediaOrder.campaignId
        });
      }

      return acc;
    }, []);

    const sources = getInheritedSources(shopGroups);
    const rows = result.map(row => applyInheritedDefault(row, sources));
    setFormattedPlatforms(rows);

    const hasInheritedDefault = rows.some(
      (row, index) =>
        row.account !== result[index].account || row.page !== result[index].page
    );
    if (hasInheritedDefault) {
      setPlatformsToSave(rows);
    }
  };

  const fetchData = async () => {
    const [
      foundMediaOrders,
      foundLineItems,
      foundAds,
      shopGroups,
      allConnexions,
      foundMediaOrderObjectives,
      foundUpsertTasks
    ] = await Promise.all([
      ACTIVATION.getMediaOrdersByCampaignId(campaignId),
      ACTIVATION.getLineItemsByCampaignId(campaignId),
      ACTIVATION.getCreativesByCampaignId(campaignId),
      ACTIVATION.getActivationShopGoups(),
      CONNEXION.getConnexions(),
      ACTIVATION.getMediaObjectives(),
      ACTIVATION.getCampaignUpsertTasks(campaignId)
    ]);
    setUpsertTasks(foundUpsertTasks);

    const availableCreativesIds = foundAds.map(el => el.availableCreativeId);
    const uniqueCreativesSet = new Set(availableCreativesIds);
    const uniqueCreativesIds = [...uniqueCreativesSet];
    const [
      foundAvailableCreatives,
      masterCampaignSetupTask,
      foundCampaignSetupTasks,
      foundActivableMedias,
      foundImportModels
    ] = await Promise.all([
      ACTIVATION.getAvailableCreativeByIds(uniqueCreativesIds),
      ACTIVATION.getCampaignLastSetupTasks([campaignId]),
      ACTIVATION.getCampaignSetupTaskByCampaignId({
        ids: [campaignId]
      }),
      ACTIVATION.getActivableMedias(),
      IMPORTMODEL.get()
    ]);
    formatPlatform(
      foundActivableMedias,
      foundMediaOrders,
      foundImportModels,
      shopGroups
    );
    const group = config.groups.find(el => el._id === campaign.groupId);
    setOrganisation(group?.name || 'Custom BO');
    setConnexions(allConnexions);
    setActivationShopGroups(shopGroups);
    setMediaOrderObjectives(foundMediaOrderObjectives);

    const medias = foundMediaOrders.map(el => el.activableMedia);
    setCanLinkMeta(['instagram', 'facebook'].every(e => medias.includes(e)));
    const imageCreatives = foundAvailableCreatives.filter(
      el => el.creativeType === 'image'
    );
    setIsMetaLinked(campaign?.isMetaLinked);

    setCampaignSetupTasks(masterCampaignSetupTask);
    setActivableMedias(foundActivableMedias);
    setImportModels(foundImportModels);
    const foundMediaOrdersState = [];
    const canSetupMediaOrderIds = [];
    const inProgressTask = foundCampaignSetupTasks.find(
      el => !['done', 'error', 'aborted'].includes(el.status)
    );
    for await (const mediaOrder of foundMediaOrders) {
      const setupTask = await getSetupTask({
        foundCampaignSetupTasks,
        mediaOrderId: mediaOrder._id,
        masterCampaignSetupTask: foundCampaignSetupTasks.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        )
      });

      const activableMedia = foundActivableMedias.find(
        el => el.key === mediaOrder.activableMedia
      );
      if (
        activableMedia?.canSetUp &&
        ((inProgressTask && setupTask?.status) ||
          (!inProgressTask &&
            setupTask?.mediaOrderStatus?.[mediaOrder._id]?.status !== 'done'))
      ) {
        canSetupMediaOrderIds.push(mediaOrder.id);
      }
      foundMediaOrdersState.push({
        ...mediaOrder,
        budgetMedia: getBudgetMediaForMediaOrder({
          media: mediaOrder.activableMedia,
          withDataPlan: campaign.withDataPlan
        }),
        externalId: mediaOrder.externalId?.externalId,
        isExpanded: true,
        isEditing: false,
        displaySetup: getDisplaySetup({
          mediaOrder,
          allActivableMedias: foundActivableMedias,
          allImportModels: foundImportModels
        }),
        setupTask,
        setup: getSetup({
          mediaOrder,
          allActivableMedias: foundActivableMedias,
          allImportModels: foundImportModels,
          allCampaignSetupTasks: foundCampaignSetupTasks,
          masterCampaignSetupTask
        })
      });
    }
    setSelectedMediaOrderIds(canSetupMediaOrderIds);
    setMediaOrders(foundMediaOrdersState);
    setLineItems(
      foundLineItems.map(lineItem => ({
        ...lineItem,
        budgetMedia: getBudgetMediaForLineItem({
          foundMediaOrders: foundMediaOrdersState,
          foundLineItems,
          lineItem
        }),
        externalId: lineItem.externalId?.externalId,
        isExpanded: true,
        isEditing: false
      }))
    );

    const postCreatives = foundAvailableCreatives.filter(
      el => el.creativeType === 'post' && el.postId && el.pageId && el.groupId
    );

    const blockedPagesByMedias = [];
    const postByCreativeId = {};
    if (postCreatives.length > 0) {
      const results = await Promise.all(
        postCreatives.map(async creative => {
          const foundAd = foundAds.find(
            ad => ad.availableCreativeId === creative._id
          );
          const foundMediaOrder = foundMediaOrders.find(
            mediaOrder => mediaOrder._id === foundAd?.mediaOrderId
          );
          const media = foundMediaOrder?.activableMedia;
          const response = media
            ? await ACTIVATION.fetchMetaPosts({
                postId: creative.postId,
                pageId: creative.pageId,
                groupId: creative.groupId,
                media
              })
            : { posts: [] };

          if (media && !blockedPagesByMedias.includes(media)) {
            blockedPagesByMedias.push(media);
          }

          return {
            creativeId: creative._id,
            post: response?.posts?.[0] || null
          };
        })
      );
      results.forEach(({ creativeId, post }) => {
        if (post) {
          postByCreativeId[creativeId] = post;
        }
      });
    }
    setBlockedPagesMedias(blockedPagesByMedias);

    setAds(
      foundAds.map(ad => {
        const availableCreative = foundAvailableCreatives.find(
          el => el._id === ad.availableCreativeId
        );
        const postData = postByCreativeId[ad.availableCreativeId];
        return {
          ...ad,
          availableCreative,
          postData,
          externalId: ad.externalId?.externalId,
          isExpanded: true,
          isEditing: false,
          isChecked: ad.publicInventory || false
        };
      })
    );

    setAvailableCreatives(imageCreatives);

    const shops = await getInsightShops(foundLineItems);

    setInsightShops(shops);
    if (campaign.skipData) {
      const canSetupSomeMedias = foundMediaOrdersState.some(el => {
        const foundMedia = foundActivableMedias.find(
          media => el.activableMedia === media.key
        );
        return foundMedia?.geoTarget?.activated;
      });
      setCanSetupGlobally(canSetupSomeMedias);
    } else {
      setCanSetupGlobally(true);
    }

    setLoaded(true);
  };

  const toggleExpandedCampaignSetup = key => {
    if (key === 'all') {
      setIsExpandedSetupCampaign(!isExpandedSetupCampaign);
      return;
    }
    const newOrders = [...mediaOrders];
    const foundOrder = newOrders.find(el => el.id === key);
    foundOrder.isExpanded = !foundOrder.isExpanded;
    setMediaOrders(newOrders);
  };

  const downloadAssets = async () => {
    const id = campaign.id || campaign._id;

    try {
      const allAssets = await ACTIVATION.downloadAssetsByCampaignId(id);

      if (allAssets) {
        const blob = new Blob([allAssets], {
          type: 'application/zip'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'assets.zip';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to retrieve the assets.');
      }
    } catch (error) {
      console.error('Error downloading assets:', error);
    }
  };

  const getActionLabel = status => {
    switch (status) {
      case 'toDo':
        return 'En cours';
      case 'uploadingSegments':
        return 'Export des segments';
      case 'segmentsExported':
        return 'Segment exporté';
      case 'uploadingCreatives':
        return 'Export des visuels';
      case 'creativesUploaded':
        return 'Visuels exportés';
      case 'creatingOrder':
        return 'Création de la campagne';
      case 'creatingLineItems':
        return 'Création des ensembles de publicités';
      case 'attachingAds':
        return 'Création des publicités';
      case 'done':
        return 'Synchronisé';
      default:
        return status;
    }
  };

  const getStatusChip = mediaOrderId => (activableMedia, setupTask = {}) => {
    if (activableMedia.canSetUp !== true) {
      return (
        <Chip
          label="Setup manuel"
          icon={<ReportProblemIcon />}
          style={{
            backgroundColor: themeColors.badgeDomains,
            borderRadius: '3px',
            fontWeight: 600,
            fontSize: '12px',
            marginLeft: '18px',
            marginRight: '18px'
          }}
        />
      );
    }

    if (!setupTask) {
      return (
        <Chip
          label="Synchronisable"
          icon={<ErrorOutlineIcon />}
          style={{
            backgroundColor: themeColors.badgeFunnel,
            borderRadius: '3px',
            fontWeight: 600,
            fontSize: '12px',
            marginLeft: '18px',
            marginRight: '18px'
          }}
        />
      );
    }

    const taskRef = setupTask.mediaOrderStatus?.[mediaOrderId] || setupTask;

    switch (taskRef.status) {
      case 'done':
        return (
          <Chip
            label="Synchronisé"
            icon={<CheckIcon />}
            style={{
              backgroundColor: themeColors.badgeActif,
              borderRadius: '3px',
              fontWeight: 600,
              fontSize: '12px',
              marginLeft: '18px',
              marginRight: '18px'
            }}
          />
        );

      case 'error':
        return (
          <Tooltip title={taskRef.errorMsg}>
            <Chip
              label="Erreur"
              icon={<CancelOutlinedIcon />}
              style={{
                backgroundColor: themeColors.error_gestion_campagne,
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '12px',
                marginLeft: '18px',
                marginRight: '18px'
              }}
            />
          </Tooltip>
        );
      case 'aborted':
        return (
          <Tooltip title={taskRef.errorMsg}>
            <Chip
              label="Annulé"
              icon={<CancelOutlinedIcon />}
              style={{
                backgroundColor: themeColors.error_gestion_campagne,
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '12px',
                marginLeft: '18px',
                marginRight: '18px'
              }}
            />
          </Tooltip>
        );
      case 'partial':
        return (
          <Chip
            label="Setup manuel"
            icon={<CheckIcon />}
            style={{
              backgroundColor: themeColors.badgeActif,
              borderRadius: '3px',
              fontWeight: 600,
              fontSize: '12px',
              marginLeft: '18px',
              marginRight: '18px'
            }}
          />
        );
      // TODO : Remove status
      // on reçoit des campaignSetupTask ou des mediaSetupTask, les status sont diff.
      case 'toDo':
      case 'uploadingSegments':
      case 'uploadInProgress':
      case 'segmentsExported':
      case 'uploadingCreatives':
      case 'creativesUploaded':
      case 'SettingUpCampaign':
      case 'creatingOrder':
      case 'orderCreated':
      case 'creatingLineItems':
      case 'lineItemsCreated':
      case 'attachingAds':
        return (
          <Tooltip title={getActionLabel(taskRef.status)}>
            <Chip
              label="En cours"
              style={{
                backgroundColor: themeColors.grey_light_button,
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '12px',
                marginLeft: '18px',
                marginRight: '18px'
              }}
            />
          </Tooltip>
        );

      default:
        return (
          <Chip
            label="Synchronisable"
            icon={<ErrorOutlineIcon />}
            style={{
              backgroundColor: themeColors.badgeFunnel,
              borderRadius: '3px',
              fontWeight: 600,
              fontSize: '12px',
              marginLeft: '18px',
              marginRight: '18px'
            }}
          />
        );
    }
  };

  const getCampaignSetupTasks = async () => {
    const masterCampaignTask = await ACTIVATION.getCampaignLastSetupTasks([
      campaignId
    ]);

    if (masterCampaignTask) {
      setCampaignSetupTasks(masterCampaignTask);
    }

    const allCampaignSetupTasks = await ACTIVATION.getCampaignSetupTaskByCampaignId(
      { ids: [campaignId] }
    );
    const newmediaOrders = [];
    for await (const mediaOrder of mediaOrders) {
      const setupTask = await getSetupTask({
        foundCampaignSetupTasks: allCampaignSetupTasks,
        mediaOrderId: mediaOrder._id,
        masterCampaignSetupTask: allCampaignSetupTasks.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        )
      });

      newmediaOrders.push({
        ...mediaOrder,
        setupTask,
        setup: getSetup({
          mediaOrder,
          allActivableMedias: activableMedias,
          allImportModels: importModels,
          allCampaignSetupTasks,
          masterCampaignSetupTask: allCampaignSetupTasks.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
          )
        })
      });
    }
    setMediaOrders(newmediaOrders);
  };

  const getUpsertTasks = async () => {
    const foundUpsertTasks = await ACTIVATION.getCampaignUpsertTasks(
      campaignId
    );
    setUpsertTasks(foundUpsertTasks);
  };

  const refreshSetupState = () => {
    getCampaignSetupTasks();
    getUpsertTasks();
  };

  const reloadAppliedGeoTargeting = async () => {
    const foundLineItems = await ACTIVATION.getLineItemsByCampaignId(
      campaignId
    );
    setLineItems(currentLineItems =>
      currentLineItems.map(lineItem => {
        const foundLineItem = foundLineItems.find(el => el.id === lineItem.id);
        if (!foundLineItem) {
          return lineItem;
        }
        return {
          ...lineItem,
          filter: foundLineItem.filter,
          audience: foundLineItem.audience
        };
      })
    );
    const shops = await getInsightShops(foundLineItems);
    setInsightShops(shops);
  };

  const launchSetupCampaign = async () => {
    await ACTIVATION.launchCampaignSetup(campaignId, selectedMediaOrderIds);
    setMediaOrders(
      mediaOrders.map(el => {
        return {
          ...el,
          setup: false
        };
      })
    );
    refreshSetupState();
  };

  const abortSetup = async mediaOrder => {
    await ACTIVATION.abortCampaignSetup({
      campaignConfigsId: campaignId,
      mediaOrder
    });
    refreshSetupState();
  };

  const handleSaveMediaOrder = async mediaOrder => {
    const order = await ACTIVATION.getMediaOrderById(mediaOrder.id);
    order.externalId = { externalId: mediaOrder?.externalId };
    order.objective = mediaOrder?.objective || '';
    order.subObjective = mediaOrder?.subObjective || '';
    order.geoTarget = mediaOrder?.geoTarget;
    order.marginRate = mediaOrder?.marginRate;
    order.conversionLocation = mediaOrder?.conversionLocation;
    await ACTIVATION.updateMediaOrderById(mediaOrder.id, order);
  };

  const handleSaveLineItem = async (id, newExternalId) => {
    const lineItem = await ACTIVATION.getLineItemById(id);
    if (lineItem.externalId) {
      lineItem.externalId.externalId = newExternalId;
    } else {
      lineItem.externalId = {
        externalId: newExternalId
      };
    }
    await ACTIVATION.updateLineItemById(id, lineItem);
  };

  const handleSaveExternalId = async (id, newExternalId, newLeadGenId) => {
    const ad = await ACTIVATION.getCreativeById(id);
    if (ad.externalId) {
      ad.externalId.externalId = newExternalId;
    } else {
      ad.externalId = {
        externalId: newExternalId
      };
    }
    if (newLeadGenId) {
      ad.leadGenFormId = newLeadGenId;
    }
    await ACTIVATION.updateCreativeById(id, ad);
  };

  const handleSaveAd = async (id, payload) => {
    await ACTIVATION.updateCreativeById(id, payload);
  };

  const savePlatform = async newPlatforms => {
    const platforms = newPlatforms.reduce((acc, formattedPlatform) => {
      if (formattedPlatform.page) {
        acc.push(formattedPlatform.page);
      }
      if (formattedPlatform.account) {
        acc.push(formattedPlatform.account);
      }
      return acc;
    }, []);
    await ACTIVATION.updateCampaignConfig(campaignId, {
      platforms
    });
  };

  const linkMeta = async boolMeta => {
    setIsMetaLinked(boolMeta);
    await ACTIVATION.updateCampaignConfig(campaignId, {
      isMetaLinked: boolMeta
    });
  };

  const getMediaModelByMedia = media => {
    const activableMediaFounded = activableMedias.find(
      activableMedia => activableMedia.key === media
    );

    const importModelFounded = importModels.find(
      importModel => importModel.connectorName === activableMediaFounded.model
    );

    return importModelFounded.model;
  };

  const displayDrawerInput = key => {
    const getCertified = data => {
      if (data === null) {
        return null;
      }

      const ensureObj = d => {
        if (d?.config && typeof d.config === 'object') return d;
        return {
          ...d,
          type: d.model ? 'account' : 'page',
          config: {
            model:
              d?.config?.model || d?.model || getMediaModelByMedia(d.media),
            status: 'validated',
            config: { externalId: '', connexionId: null }
          }
        };
      };

      const shaped = ensureObj(data);

      if (shaped.media === 'sms') {
        if (shaped?.config?.connexionId === '') {
          return {
            ...shaped,
            config: { ...shaped.config, __status: 'noExist' }
          };
        }
      } else {
        if (shaped?.config?.config?.externalId === '') {
          return {
            ...shaped,
            config: { ...shaped.config, __status: 'noExist' }
          };
        }
      }
      return shaped;
    };

    const newPlatforms = formattedPlatforms.map(el => {
      if (el._id === key) {
        return {
          ...el,
          account: getCertified(el.account),
          page: getCertified(el.page),
          drawer: !el.drawer
        };
      }
      return { ...el };
    });
    setFormattedPlatforms(newPlatforms);

    if (!newPlatforms.some(el => el.drawer) && updatePlatforms) {
      savePlatform(newPlatforms);
    }
  };

  const isObject = data => {
    return data !== null && typeof data === 'object';
  };

  const handleChangeExternalId = (type, media) => value => {
    let tmp = [];
    const activableMediaModel =
      activableMedias.find(el => el.key === media)?.model || null;

    const pickedConnexionIdFromValue =
      value?.config?.connexionId ?? value?.connexionId ?? null;

    const fallbackConnexionId =
      connexions.find(el => el.model === activableMediaModel)?._id || null;

    const selectedConnexionId =
      pickedConnexionIdFromValue ?? fallbackConnexionId;

    if (media === 'sms') {
      tmp = formattedPlatforms.map(formattedPlatform => {
        if (formattedPlatform.media === media) {
          if (isObject(formattedPlatform[type].config)) {
            formattedPlatform[type].config = {
              connexionId: value?.key,
              name: value?.label
            };
          } else {
            formattedPlatform[type] = {
              media,
              type,
              config: {
                connexionId: value?.key,
                name: value?.label
              }
            };
          }
        }
        return formattedPlatform;
      });
    } else {
      tmp = formattedPlatforms.map(formattedPlatform => {
        if (formattedPlatform.media === media) {
          const base = {
            externalId: value?.key,
            connexionId: selectedConnexionId,
            ...value?.config
          };

          if (isObject(formattedPlatform[type]?.config)) {
            if (isObject(formattedPlatform[type]?.config?.config)) {
              formattedPlatform[type].config = {
                model: formattedPlatform[type].config.model,
                name: value?.group ? value.group : organisation,
                status: 'validated',
                config: base,
                type
              };
              formattedPlatform[type].type = type;
            } else {
              formattedPlatform[type].config = { config: base };
              formattedPlatform[type].type = type;
            }
          } else {
            formattedPlatform[type] = {
              media,
              type,
              config: {
                model: getMediaModelByMedia(media),
                name: value?.group ? value.group : organisation,
                status: 'validated',
                config: base,
                type
              }
            };
          }
        }
        return formattedPlatform;
      });
    }

    if (value?.group) {
      setOrganisation(value.group);
    }
    setFormattedPlatforms(tmp);
  };

  const handleCampaignOptions = (camp, opt) => {
    if (!opt) {
      return;
    }

    const typeOfPrice = {
      media: 'percentMedia',
      fix: 'fix',
      total: 'percentTotal'
    };

    const calculatePrice = (typeOfBudget, percentage) => {
      const budget = Math.round(
        typeOfBudget === typeOfPrice.total
          ? camp.budget
          : camp.withDataPlan.budget.media
      );

      return Math.round(budget * (percentage / 100));
    };

    let optionPrice = 0;

    const { type, amount } = opt.price;

    switch (type) {
      case typeOfPrice.fix:
        optionPrice = amount;
        break;
      default:
        optionPrice = calculatePrice(type, amount);
        break;
    }
    // eslint-disable-next-line consistent-return
    return opt.amount ? opt.amount : optionPrice;
  };

  const sumAndRoundBudget = camp => {
    const {
      budgetOptions = 0,
      withDataPlan: {
        budget: {
          media = 0,
          technicalFees = 0,
          data = 0,
          functionalFees = 0,
          margin = 0
        }
      }
    } = camp;

    const total =
      media + technicalFees + data + functionalFees + budgetOptions - margin;

    return Math.round(total);
  };

  const invoiceItemConfigs = [
    {
      label: "Achat d'espace",
      budgetKey: 'media',
      amount: campaign.withDataPlan?.budget?.media
    },
    {
      label: 'Data',
      budgetKey: 'data',
      amount: campaign.withDataPlan?.budget?.data
    },
    {
      label: 'Frais de routage',
      budgetKey: 'functionalFees',
      amount: campaign.withDataPlan?.budget?.functionalFees
    },
    {
      label: 'Frais techniques',
      budgetKey: 'technicalFees',
      amount: campaign.withDataPlan?.budget?.technicalFees
    },
    {
      label: 'Marge',
      budgetKey: 'margin',
      amount: campaign.withDataPlan?.budget?.margin,
      showItem: !!campaign.withDataPlan?.budget?.margin
    }
  ];

  const globalMargin = campaign.withDataPlan?.budget?.margin || 0;
  const mediaOrderMargin = globalMargin / mediaOrders.length;

  const findCampaignLabelByMedia = (platform, campaignObjectiveKey) => {
    const objective = platform?.objectives?.find(
      obj => obj.objectiveKey === campaignObjectiveKey
    );

    const subObjective = objective.subObjectives[0];

    if (objective) {
      if (subObjective) {
        return `${objective.objectiveLabel} - ${subObjective.subObjectiveLabel}`;
      }
      return `${objective.objectiveLabel}`;
    }

    return null;
  };

  const filterObjectives = (objectives, labels) => {
    const result = {
      platforms: labels?.platforms?.map(platform => {
        const platformObjectives = objectives?.filter(mediaObjective =>
          platform?.platforms.includes(mediaObjective?.platform)
        );

        const filteredObjectives = platform?.objectives
          ?.map(objective => {
            const matchingObjective = platformObjectives?.find(
              mediaObjective =>
                mediaObjective?.objectiveKey === objective?.objectiveKey
            );

            if (matchingObjective) {
              const conversionLocations = enrichConversionLocations(
                matchingObjective,
                objective
              );

              return {
                ...objective,
                conversionLocations,
                subObjectives: objective?.subObjectives.filter(subObjective =>
                  matchingObjective?.subObjectives?.includes(
                    subObjective?.subObjectiveKey
                  )
                )
              };
            }

            return null; // If no match add null
          })
          .filter(Boolean); // Remove falsy values => null in this case

        return {
          ...platform,
          objectives: filteredObjectives,
          campaignObjectiveLabel: findCampaignLabelByMedia(
            platform,
            campaignObjective
          )
        };
      })
    };

    return result;
  };

  const filteredObjectives = filterObjectives(
    mediaOrderObjectives,
    mockedObjectives
  );

  const formatOptions = () => {
    const paddingLeft = campaign.options?.length > 1 ? '40px' : null;
    const listCampaignOptions =
      campaign.options?.map(option => (
        <InvoiceItem
          paddingLeft={paddingLeft}
          label={option.name}
          amount={numeral(handleCampaignOptions(campaign, option)).format(
            '0,0 $'
          )}
        />
      )) || [];
    return (
      <>
        {listCampaignOptions.length > 1 && (
          <Item justify="flex-start">
            <Item flex>
              <Text>Options</Text>
            </Item>
            <Item
              xs
              justify="flex-start"
              style={{
                paddingRight: '5px',
                paddingLeft: '5px'
              }}
            >
              <Divider
                color="light"
                noFade
                style={{
                  width: '100%',
                  height: '0.5px'
                }}
              />
            </Item>
            <Item flex>
              <Text fontWeight={700}>{`${numeral(
                campaign?.budgetOptions
              ).format('0,0 $')}`}</Text>
            </Item>
          </Item>
        )}
        {listCampaignOptions}
      </>
    );
  };

  const getShortDepartmentName = name => {
    const baseName = name.split(' - ')[0];
    const match = baseName.match(/^(\d{2}[A-B]?)/);
    return match ? match[1] : baseName;
  };

  const renderLocationAndCategories = mediaOrder => {
    return (
      <>
        {matchedDepartments?.length > 0 && (
          <Tooltip
            style={{ marginRight: '20px' }}
            title={
              <Item>
                {matchedDepartments.map(location => (
                  <Row spacing={0} key={location.key}>
                    <Item>
                      <Text color="inherit">{location.name}</Text>
                    </Item>
                  </Row>
                ))}
              </Item>
            }
          >
            <Row alignItems="start" spacing={1}>
              <ErrorOutlineIcon />
              <Text bold>
                Département :{' '}
                {matchedDepartments
                  .slice(0, 3)
                  .map(loc => getShortDepartmentName(loc.name))
                  .join(' / ')}
                {matchedDepartments.length > 3 &&
                  ` + ${matchedDepartments.length - 3} autre${
                    matchedDepartments.length - 3 > 1 ? 's' : ''
                  }`}
              </Text>
            </Row>
          </Tooltip>
        )}
        {insightUserTarget?.length > 0 && (
          <Tooltip
            style={{ marginRight: '20px' }}
            title={insightUserTarget.map(item => {
              return (
                <Row style={{ flexWrap: 'wrap' }}>
                  <Text>{item.name}</Text>
                </Row>
              );
            })}
            disabled={insightUserTarget.length < 2}
          >
            <Row alignItems="start">
              <ErrorOutlineIcon />
              <Text bold>{insightUserTarget[0]?.name}</Text>
            </Row>
          </Tooltip>
        )}
        {!!activationCategoryKeys[mediaOrder.activableMedia]?.length && (
          <Tooltip
            style={{ marginRight: '20px' }}
            title={
              <Item>
                {activationCategoryKeys[mediaOrder.activableMedia].map(item => {
                  return (
                    <Row spacing={0}>
                      <Item>
                        <Text color="inherit">{item}</Text>
                      </Item>
                    </Row>
                  );
                })}
              </Item>
            }
            disabled={
              activationCategoryKeys[mediaOrder.activableMedia].length < 2
            }
          >
            <Row alignItems="start" spacing={1}>
              <ErrorOutlineIcon />
              <Text bold>
                {activationCategoryKeys[mediaOrder.activableMedia][0]}
                {activationCategoryKeys[mediaOrder.activableMedia].length > 1 &&
                  ` et ${activationCategoryKeys[mediaOrder.activableMedia]
                    .length - 1} ${pluralize(
                    'autre',
                    activationCategoryKeys[mediaOrder.activableMedia].length
                  )}`}
              </Text>
            </Row>
          </Tooltip>
        )}
      </>
    );
  };

  const formatGeozoneDisplay = ({ parameters, audienceType }) => {
    const codes = Object.values(parameters).flat();
    const firstCode = codes[0]?.key || codes[0] || '';
    const displayCount = firstCode.length <= 5 ? 2 : 1;
    const displayedCodes = codes.slice(0, displayCount);
    const remainingCount = codes.length - displayCount;

    return (
      <Tooltip
        style={{ marginRight: '20px' }}
        title={
          <Item>
            {codes.map(code => (
              <Row spacing={0} key={code?.key || code}>
                <Item>
                  <Text color="inherit">
                    {code?.key || code} {code?.name ? `- ${code.name}` : ''}
                  </Text>
                </Item>
              </Row>
            ))}
          </Item>
        }
        disabled={codes.length < 2}
      >
        <Row alignItems="start" spacing={1}>
          <ErrorOutlineIcon />
          <Text bold>
            {audienceType} :{' '}
            {displayedCodes.map(code => code?.key || code).join(' / ')}
            {remainingCount > 0 &&
              ` + ${remainingCount} ${pluralize('autre', remainingCount)}`}
          </Text>
        </Row>
      </Tooltip>
    );
  };

  const formatZDCDisplay = shop => {
    if (!shop?.parameters) {
      return null;
    }

    let displayText = '';

    switch (shop.audienceType) {
      case 'isochrony':
        displayText = (
          <Tooltip
            style={{ marginRight: '20px' }}
            title={
              <Item>
                <Row spacing={0}>
                  <Item>
                    <Text color="inherit">
                      {numeral(shop.parameters.time).format('0[.]0')} min.{' '}
                      {shop.parameters.transportation}
                    </Text>
                  </Item>
                </Row>
              </Item>
            }
          >
            <Row alignItems="start" spacing={1}>
              <ErrorOutlineIcon />
              <Text bold>
                Isochronie : {numeral(shop.parameters.time).format('0[.]0')}{' '}
                min. {shop.parameters.transportation}
              </Text>
            </Row>
          </Tooltip>
        );
        break;
      case 'radius':
        displayText = (
          <Tooltip
            style={{ marginRight: '20px' }}
            title={
              <Item>
                <Row spacing={0}>
                  <Item>
                    <Text color="inherit">
                      {numeral(shop.parameters.distance).format('0[.]0')}{' '}
                      {shop.parameters.unit}
                    </Text>
                  </Item>
                </Row>
              </Item>
            }
          >
            <Row alignItems="start" spacing={1}>
              <ErrorOutlineIcon />
              <Text bold>
                Rayon : {numeral(shop.parameters.distance).format('0[.]0')}{' '}
                {shop.parameters.unit}
              </Text>
            </Row>
          </Tooltip>
        );
        break;
      default:
        displayText = formatGeozoneDisplay(shop);
    }

    return displayText;
  };

  const formatZDCInsighShops = shops => {
    if (!shops || !shops?.length) {
      return null;
    }

    return shops.map(shop => formatZDCDisplay(shop));
  };

  const onMediaOrderSelectedChange = mediaOrderId => {
    if (selectedMediaOrderIds.includes(mediaOrderId)) {
      setSelectedMediaOrderIds(prev => {
        const list = [...prev];
        return list.filter(el => el !== mediaOrderId);
      });
    } else {
      setSelectedMediaOrderIds(prev => {
        const list = [...prev];
        list.push(mediaOrderId);
        return list;
      });
    }
  };

  useEffect(() => {
    if (campaign?.filters?.length > 0 && matchedDepartments.length === 0) {
      getDepartments(campaign?.country || 'FR');
    }
    // eslint-disable-next-line
  }, [campaign?.filters, matchedDepartments.length]);

  useEffect(() => {
    const fetchDatasUser = async () => {
      const insights = await INSIGHTUSERTARGET.getInsightbyIds(
        campaign.insightUserTargetIds
      );
      setInsightUserTarget(insights);
      const stripeCustomer = await CUSTOMERS.getStripeCustomerById(
        campaign.stripeCustomerId
      );
      setMandatsCampaign(stripeCustomer?.mandats);
    };

    fetchDatasUser();
    setActivationCategoryKeys(campaign.activationCategoryKeys || {});
    setDefaultGeoZoneType(domainConfig?.defaultGeoZone?.type || null);
    setDefaultGeoZoneValues(domainConfig?.defaultGeoZone?.values || null);
    // eslint-disable-next-line
  }, [campaign]);

  useEffect(() => {
    const oneGeoTargetMediaOrder = mediaOrders.some(
      mediaOrder =>
        mediaOrder.geoTarget === true || isMeta(mediaOrder.activableMedia)
    );
    setOverrideDisabledGeoZone(!oneGeoTargetMediaOrder);

    const logosToDisplay = mediaOrders.map(mediaOrder => {
      const key =
        mediaOrder.activableMedia === 'search'
          ? 'googleads'
          : mediaOrder.activableMedia.toLowerCase();
      const matchingLogo = mediaLogos.find(
        logo => logo.key.toLowerCase() === key
      );
      return matchingLogo ? matchingLogo.base64Image : null;
    });

    setMediaLogosToDisplay(logosToDisplay);
    // eslint-disable-next-line
  }, [mediaOrders]);

  useEffect(() => {
    const isUpdate = formattedPlatforms.some(el => el.drawer);
    if (isUpdate) {
      setUpdatePlatforms(isUpdate);
    }
  }, [formattedPlatforms]);

  useEffect(() => {
    if (platformsToSave) {
      savePlatform(platformsToSave);
      setPlatformsToSave(null);
    }
    // eslint-disable-next-line
  }, [platformsToSave]);

  useEffect(() => {
    const fetchRequestedInsightShops = async () => {
      const pendingTask = findPendingUpsertTask(upsertTasks);
      const requestedFilter = pendingTask?.filters?.[0]?.filters || null;
      if (!requestedFilter) {
        setRequestedInsightShops([]);
        return;
      }
      const shops = await getInsightShops([{ filter: requestedFilter }]);
      setRequestedInsightShops(shops);
    };

    fetchRequestedInsightShops();
    // eslint-disable-next-line
  }, [upsertTasks]);

  useEffect(() => {
    const pendingTask = findPendingUpsertTask(upsertTasks);
    const pendingTaskId = pendingTask?.id || pendingTask?._id || null;
    const previousPendingTaskId = previousPendingUpsertTaskIdRef.current;
    previousPendingUpsertTaskIdRef.current = pendingTaskId;

    if (previousPendingTaskId && !pendingTaskId) {
      reloadAppliedGeoTargeting();
    }
    // eslint-disable-next-line
  }, [upsertTasks]);

  useEffect(() => {
    for (const lineItem of lineItems) {
      if (lineItem.defaultGeoZone) {
        setDefaultGeoZoneType(lineItem.defaultGeoZone?.type || null);
        setDefaultGeoZoneValues(lineItem.defaultGeoZone?.values || null);
      }
    }
  }, [lineItems]);

  useEffect(() => {
    // formatPlatform();
    fetchData();
    // eslint-disable-next-line
  }, []);

  const isCampaignSetupPending =
    campaignSetupTasks.length > 0 &&
    !['done', 'error', 'aborted'].includes(campaignSetupTasks[0].status);

  const launchButtonColor = isCampaignSetupPending
    ? themeColors.themeV2.detail
    : themeColors.activation;

  const showDefaultGeoZoneConfig =
    insightShops.length > 0 &&
    insightShops.some(
      el => !['radius', 'postalCode'].includes(el.audienceType)
    );

  const isRecapTab = selectedSection === 'recap';
  const isSetupTab = selectedSection === 'setup';

  const pendingUpsertTask = findPendingUpsertTask(upsertTasks);
  const pendingUpsertIndex = pendingUpsertTask
    ? upsertTasks.indexOf(pendingUpsertTask)
    : -1;

  const isUpsertBadgeVisible =
    Boolean(pendingUpsertTask) && !isCampaignSetupPending;

  useEffect(() => {
    if (onUpsertBadgeChange) {
      onUpsertBadgeChange(isUpsertBadgeVisible);
    }
  }, [isUpsertBadgeVisible, onUpsertBadgeChange]);

  const requestedGeoFilter = pendingUpsertTask?.filters?.[0]?.filters || null;
  const currentGeoFilter = campaign?.filters?.[0]?.filters || null;
  const isGeoTargetingChanged =
    !!requestedGeoFilter &&
    getGeoTargetingSignature(requestedGeoFilter, requestedInsightShops) !==
      getGeoTargetingSignature(currentGeoFilter, insightShops);
  const geoTargeting = isGeoTargetingChanged
    ? {
        old: getGeoTargetingLabel(currentGeoFilter, insightShops),
        new: getGeoTargetingLabel(requestedGeoFilter, requestedInsightShops)
      }
    : null;

  const pendingNameChange =
    pendingUpsertTask?.name && pendingUpsertTask.name !== campaign.name
      ? {
          old: campaign.name,
          new: pendingUpsertTask.name,
          upsertLabel: `Upsert ${pendingUpsertIndex + 1}`,
          requestedAt: pendingUpsertTask.createdAt || null
        }
      : null;

  const pendingChangesByMedia = pendingUpsertTask
    ? mediaOrders.reduce((changes, mediaOrder) => {
        const change = getMediaPendingChange({
          mediaKey: mediaOrder.activableMedia,
          mediaOrder,
          mediaAds: ads.filter(ad => ad.mediaOrderId === mediaOrder.id),
          task: pendingUpsertTask,
          campaign,
          geoTargeting
        });
        if (change) {
          changes[mediaOrder.activableMedia] = {
            ...change,
            upsertLabel: `Upsert ${pendingUpsertIndex + 1}`,
            requestedAt: pendingUpsertTask.createdAt || null
          };
        }
        return changes;
      }, {})
    : {};

  const changedCategoryMedias = pendingUpsertTask
    ? getChangedCategoryMedias(pendingUpsertTask, campaign)
    : [];

  const concernedUpsertMedias = mediaOrders.reduce((medias, mediaOrder) => {
    if (
      pendingNameChange ||
      pendingChangesByMedia[mediaOrder.activableMedia] ||
      changedCategoryMedias.includes(mediaOrder.activableMedia)
    ) {
      medias.push(mediaOrder.activableMedia);
    }
    return medias;
  }, []);

  const upsertMediaOrderIds = mediaOrders.reduce((ids, mediaOrder) => {
    const activableMedia = activableMedias.find(
      el => el.key === mediaOrder.activableMedia
    );
    if (
      activableMedia?.canSetUp === true &&
      concernedUpsertMedias.includes(mediaOrder.activableMedia)
    ) {
      ids.push(mediaOrder.id);
    }
    return ids;
  }, []);
  const upsertSelectionKey = upsertMediaOrderIds.join(',');

  useEffect(() => {
    if (
      !upsertSelectionKey ||
      upsertSelectionKey === upsertSelectionKeyRef.current
    ) {
      return;
    }
    upsertSelectionKeyRef.current = upsertSelectionKey;
    setSelectedMediaOrderIds(upsertMediaOrderIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertSelectionKey]);

  const upsertMediaBudgets = mediaOrders.reduce((budgets, mediaOrder) => {
    budgets[mediaOrder.activableMedia] = getPlanMediaBudget(
      pendingUpsertTask?.withDataPlan,
      mediaOrder.activableMedia
    );
    return budgets;
  }, {});

  const budgetVersions = campaign.withDataPlan
    ? buildBudgetVersions({
        campaign,
        upsertTasks,
        initialLines: [
          ...invoiceItemConfigs
            .filter(item => item.showItem !== false)
            .map(item => ({
              label: item.label,
              budgetKey: item.budgetKey,
              amount: item.amount
            })),
          ...(campaign.options || []).map(option => ({
            label: option.name,
            amount: handleCampaignOptions(campaign, option)
          }))
        ],
        totalHT: sumAndRoundBudget(campaign),
        initialPushedAt:
          campaignSetupTasks?.[0]?.status === 'done'
            ? campaignSetupTasks[0].updatedAt
            : null
      })
    : [];

  const view = {
    campaign,
    budgetVersions,
    pendingNameChange,
    themeColors,
    domainConfig,
    mediaOrders,
    concernedUpsertMedias,
    pendingChangesByMedia,
    upsertMediaBudgets,
    pendingUpsertPlan: pendingUpsertTask?.withDataPlan || null,
    lineItems,
    ads,
    availableCreatives,
    activableMedias,
    mediaLogosToDisplay,
    mediaOrderMargin,
    mandatsCampaign,
    getStatusChip,
    setMediaOrders,
    handleSaveMediaOrder,
    isCampaignSetupPending,
    filteredObjectives,
    selectedMediaOrderIds,
    onMediaOrderSelectedChange,
    renderLocationAndCategories,
    formatZDCInsighShops,
    findInsightShop,
    insightShops,
    requestedGeoLabel: geoTargeting?.new || null,
    requestedInsightShops,
    insightUserTarget,
    matchedDepartments,
    activationCategoryKeys,
    handleCampaignOptions,
    setLineItems,
    handleSaveLineItem,
    setAds,
    handleSaveExternalId,
    handleSaveAd,
    hasMetaPage,
    isMeta,
    isExpandedSetupCampaign,
    toggleExpandedCampaignSetup,
    loaded,
    downloadAssets,
    canLinkMeta,
    isMetaLinked,
    linkMeta,
    launchButtonColor,
    canSetupGlobally,
    launchSetupCampaign,
    abortSetup,
    formattedPlatforms,
    isExpandedSetupAccount,
    setIsExpandedSetupAccount,
    setupPendingTooltip,
    mediaLogos,
    allPlatformAccounts,
    allPlatformPages,
    displayDrawerInput,
    handleChangeExternalId,
    activationShopGroups,
    blockedPagesMedias,
    getMediaModelByMedia,
    showDefaultGeoZoneConfig,
    isExpandedDefaultGeoZone,
    setIsExpandedDefaultGeoZone,
    defaultGeoZoneType,
    geoZones,
    setDefaultGeoZoneType,
    disabledGeoZone,
    overrideDisabledGeoZone,
    defaultGeoZoneValues,
    setDefaultGeoZoneValues,
    validateDefaultGeoZone,
    setDisabledGeoZone,
    invoiceItemConfigs,
    formatOptions,
    sumAndRoundBudget
  };

  return (
    <Container>
      <IntervalTimer
        timeout={10000}
        callback={refreshSetupState}
        enabled
        repeat
      />
      <Row spacing={1} style={{ paddingBottom: '14px' }}>
        <Item justify="flex-start">
          <Text fontWeight={700} fontSize="16px">
            {campaign.name}&nbsp;
            <Text
              component="span"
              fontSize="12.5px"
              fontWeight={400}
              style={{ color: themeColors.designSystem.faint }}
            >
              ({campaignId})
            </Text>
          </Text>
        </Item>
      </Row>
      {isRecapTab && <ModalRecap view={view} />}
      {isSetupTab && <ModalSetup view={view} />}
    </Container>
  );
};

export default ModalParams;
